import { useState, useEffect, useRef } from 'react';
import { api } from '../api/client';
import { useCsvActions } from '../context/CsvActionsContext';
import { useT } from '../i18n';
import { Resource, MATURITY_OPTIONS } from '../types';
import { parseCsv } from '../utils/csvParser';
import { ClipboardList, Check } from 'lucide-react';

interface ScoredResource extends Resource {
  maturityScore: number | null;
}

export function MaturityScoringPage() {
  const { t } = useT();
  const { setCsvActions } = useCsvActions();
  const [resources, setResources] = useState<ScoredResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<Record<number, boolean>>({});
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const allScored = resources.length > 0 && resources.every(r => r.maturityScore !== null);
  const scoredCount = resources.filter(r => r.maturityScore !== null).length;

  useEffect(() => {
    const load = async () => {
      try {
        const allResources = await api.getAllQualifiedResources() as Resource[];

        // Load maturity rating for each resource
        const scored: ScoredResource[] = await Promise.all(
          allResources.map(async (r) => {
            const maturity = await api.getMaturityRating(r.id) as any;
            return {
              ...r,
              maturityScore: maturity?.score ?? null,
            };
          })
        );

        setResources(scored);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const handleMaturityChange = async (resourceId: number, score: number) => {
    const resource = resources.find(r => r.id === resourceId);
    const isToggleOff = resource?.maturityScore === score;
    const newScore = isToggleOff ? null : score;

    setSaving(prev => ({ ...prev, [resourceId]: true }));
    try {
      if (isToggleOff) {
        await api.setMaturityRating(resourceId, -1);
      } else {
        await api.setMaturityRating(resourceId, score);
      }

      setResources(prev =>
        prev.map(r => r.id === resourceId ? { ...r, maturityScore: newScore } : r)
      );
    } catch (err: any) {
      alert(err.message);
    } finally {
      setSaving(prev => ({ ...prev, [resourceId]: false }));
    }
  };

  // Register CSV actions in topbar
  useEffect(() => {
    setCsvActions({
      uploadLabel: t('csv.uploadCsv'),
      onUpload: () => fileInputRef.current?.click(),
      downloadUrl: '/api/ratings/export-maturity-csv',
      downloadLabel: t('csv.exportCsv'),
      uploading: importing,
    });
    return () => setCsvActions(null);
  }, [importing, setCsvActions, t]);

  const handleImportCsv = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    setImporting(true);
    try {
      const text = await file.text();
      const parsed = parseCsv(text);

      if (parsed.length === 0) {
        alert(t('mat.csv.empty'));
        return;
      }

      // Map CSV headers to expected fields
      const rows = parsed.map(row => ({
        resource: row['Resource'] || '',
        maturityScore: row['Maturity Score'] || '',
      }));

      const result = await api.importMaturityCsv(rows) as any;

      let msg = t('mat.import.imported')
        .replace('{n}', String(result.imported))
        .replace('{s}', result.imported !== 1 ? 's' : '');
      if (result.skipped > 0) msg += ' ' + t('mat.import.skipped').replace('{n}', String(result.skipped));
      if (result.errors?.length > 0) msg += '\n\n' + t('mat.import.warnings') + '\n' + result.errors.join('\n');
      alert(msg);

      // Reload data
      const allResources = await api.getAllQualifiedResources() as Resource[];
      const scored: ScoredResource[] = await Promise.all(
        allResources.map(async (r) => {
          const maturity = await api.getMaturityRating(r.id) as any;
          return { ...r, maturityScore: maturity?.score ?? null };
        })
      );
      setResources(scored);
    } catch (err: any) {
      alert(t('mat.import.failed') + (err.message || t('mat.import.unknownError')));
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height: 200 }}>
        <div className="spinner" />
      </div>
    );
  }

  if (resources.length === 0) {
    return (
      <div className="empty-state-card">
        <div className="empty-state">
          <div className="empty-state-icon"><ClipboardList /></div>
          <div className="empty-state-title">{t('mat.empty.title')}</div>
          <div className="empty-state-desc">{t('mat.empty.desc')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="maturity-scoring-page">
      {/* Hidden file input for CSV import */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        onChange={handleImportCsv}
      />

      {/* Fixed header with maturity level columns */}
      <div className="maturity-scoring-header">
        <div className="maturity-header-label">
          {t('mat.resourceHeader')}
          {allScored ? (
            <span className="maturity-header-status maturity-header-status--done">
              <span className="maturity-header-status-icon"><Check className="icon-sm" /></span>
              {t('mat.allScored').replace('{n}', String(resources.length))}
            </span>
          ) : (
            <span className="maturity-header-status">
              {t('mat.scoredOf').replace('{n}', String(scoredCount)).replace('{total}', String(resources.length))}
            </span>
          )}
        </div>
        <div className="maturity-header-scores">
          {MATURITY_OPTIONS.map((option) => (
            <div key={option.value} className="maturity-header-col">
              <span className="maturity-header-num">{option.value}</span>
              <span className="maturity-header-name">{option.label}</span>
              <span className="maturity-header-info">
                i
                <span className="maturity-header-tooltip">
                  {t(`mat.tooltip.${option.value}`)}
                </span>
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Scrollable resource rows */}
      <div className="maturity-resource-list">
        {resources.map((resource) => (
          <div key={resource.id} className="maturity-resource-row">
            <div className="maturity-resource-card">
              <div className="maturity-resource-title">{resource.title}</div>
              {resource.description && (
                <div className="maturity-resource-desc">{resource.description}</div>
              )}
            </div>
            <div className="maturity-score-buttons">
              {MATURITY_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`maturity-score-btn ${resource.maturityScore === option.value ? 'selected' : ''} maturity-${option.value}`}
                  onClick={() => handleMaturityChange(resource.id, option.value)}
                  disabled={saving[resource.id]}
                >
                  {option.value}
                </button>
              ))}
              {saving[resource.id] && <div className="spinner spinner-sm" />}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
